import { useEffect, useState, useMemo, useCallback, Fragment, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"
import dayjs from "dayjs";
import {
    getEventBySlug,
    rsvpToEvent,
    markEventAsInterested,
    getUserInterestedEventsByUserId
} from "../../app/store/slices/event/event";
import { setInviteLinkUserDetails } from "../../app/store/slices/event/invitation";
import { getTicketsByEventId } from "../../app/store/slices/ticket/ticket";
import { resetTicketBuyingState } from "../../app/store/slices/ticket/ticketBuying";
import useAuthNavigation from "../../view/authentication/useAuthNavigation";
import { formatEventDate } from "../../utils/dateFormatter";
import { EVENT_TYPES } from "./useEventStatus";
import { Body2 } from "../../themes/typography";
import { errorToast, successToastTopRight } from "../../utils/toasts/toasts";

export default function useEventSpecificDetails() {
    const { eventSlug } = useParams();

    const dispatch = useDispatch();
    const { inviteLinkUserDetails } = useSelector((state) => state.invitation);
    const { user } = useSelector((state) => state.auth);
    const { userInterestedEvents } = useSelector((state) => state.event);
    // console.log("Invite User Details", inviteLinkUserDetails)
    // console.log("userInterestedEvents ->", userInterestedEvents);

    const { openAuth } = useAuthNavigation();

    const eventScheduleRef = useRef(null);

    const [eventData, setEventData] = useState(null);
    const [showTickets, setShowTickets] = useState(false);

    const [showRsvp, setShowRsvp] = useState(false);
    const [showRsvpResponseMessage, setShowRsvpResponseMessage] = useState(false);

    const [isUserMarkEventAsInterested, setIsUserMarkEventAsInterested] = useState(false);

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);


    /**
     * The scrollToEventSchedule function is wrapped in useCallback to memoize it and prevent unnecessary re-creations on every render.
     * It scrolls the page to the event schedule section smoothly.
     */
    const scrollToEventSchedule = useCallback(() => {
        if (eventScheduleRef.current) {
            eventScheduleRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);


    /**
     * @async function :to fetches event data based on the provided slug.
     * @param {string} slug - The unique identifier for the event.
     * @description
     * 1. Retrieves the session ID from local storage.
     * 2. Dispatches an action to fetch event details using the slug.
     * 3. If event data is found, it fetches associated tickets.
     * 4. If a session ID exists, it checks the RSVP status for the user.
     */
    async function fetchEvent(slug) {
        try {

            // Step 2
            const eventResponse = await dispatch(getEventBySlug(slug)).unwrap();

            // Step 3
            if (eventResponse?.data) {
                const ticketsResponse = await dispatch(getTicketsByEventId(eventResponse?.data?.eventId)).unwrap();

                if (ticketsResponse?.code === 200 && eventResponse?.code === 200) {
                    setEventData({
                        ...eventResponse?.data,
                        tickets: ticketsResponse?.data
                    });
                }
            }

            const reply = inviteLinkUserDetails?.response === 'yes' || inviteLinkUserDetails?.response === 'no'

            if (inviteLinkUserDetails?.type === "attendee" && reply) {
                setShowRsvp(false);
            } else if (inviteLinkUserDetails === null) {
                setShowRsvp(false);
            } else {
                setShowRsvp(true);
            }

        } catch (error) {
            console.error("Error fetching event data:", error);
            setError({
                code: error?.status || 500,
                data: error?.data || { message: "An unexpected error occurred." },
                message: error?.statusText || "An unexpected error occurred."
            });
        } finally {
            setLoading(false);
        }
    }

    /**
     * Fetches the event data when the `eventSlug` value changes.
     */
    useEffect(() => {
        if (eventSlug) {
            dispatch(resetTicketBuyingState());
            fetchEvent(eventSlug);
        }
    }, [eventSlug]);

    useEffect(() => {
        if (user && user?.id) {
            dispatch(getUserInterestedEventsByUserId(user?.id))
        }
    }, [user])



    /**
     * Toggles the visibility of the ticket information.
     * Wrapped in useCallback to memoize the function and prevent unnecessary re-creations on every render.
     */
    const getTickets = useCallback(() => {
        setShowTickets(!showTickets);
    }, [showTickets]);


    /**
     * Get the event date and show them in different format based on event types.
     * Wrapped in useMemo to optimize performance by memoizing the result.
     */
    const eventDate = useMemo(() => {
        if (eventData) {
            const eventType = eventData?.event_type;
            const start = formatEventDate(eventData?.start_date);
            const end = formatEventDate(eventData?.end_date);
            switch (eventType) {
                case EVENT_TYPES.singleDay:
                    return start;
                case EVENT_TYPES.multiDay:
                    return `${start} - ${end}`;
                case EVENT_TYPES.continuous:
                    return `${start} to ${end}`;
                default:
                    return `${start} to ${end}`;
            }
        }
        return null;
    }, [eventData?.event_type, eventData?.start_date, eventData?.end_date]);


    /**
     * Get the event formats, sub categories and categories.
     * Wrapped in useMemo to optimize performance by memoizing the result.
     */
    // const fcs = useMemo(() => {
    //     if (eventData) {
    //         const result = [];
    //         if (eventData?.formats?.[0]) result.push(eventData.formats[0]);
    //         if (eventData?.subcategories?.[0]) result.push(eventData.subcategories[0]);
    //         if (eventData?.category) result.push(eventData.category);
    //         return result;
    //     }
    //     return [];
    // }, [eventData?.formats, eventData?.category, eventData?.subcategories]);
    const fcs = useMemo(() => {
        if (eventData) {
            const result = [];
            if (eventData?.formats?.[0]) result.push(eventData.formats[0]);
            if (eventData?.subcategories?.[0]) result.push(eventData.subcategories[0]);
            if (eventData?.category) result.push(eventData.category);

            // Sort by length (descending)
            return result.sort((a, b) => b.length - a.length);
        }
        return [];
    }, [eventData?.formats, eventData?.category, eventData?.subcategories]);



    /**
     * Get the minimum ticket price from the event data by calculating the price from each ticket.
     * Wrapped in useMemo to optimize performance by memoizing the result.
     */
    const minimumTicketPrice = useMemo(() => {
        if (eventData && eventData?.tickets && eventData?.tickets.length > 0) {
            return Math.min(...eventData.tickets.map(ticket => Number(ticket.price)));
        }
        return null;
    }, [eventData, showTickets]);



    /**
     * Get the event timing information.
     * Wrapped in useCallback to memoize the function and prevent unnecessary re-creations on every render.
     */
    const eventTimingUI = useCallback(() => {
        if (!eventData) return null;
        const eventType = eventData?.event_type;
        const startTime = eventData?.start_time;
        const endTime = eventData?.end_time;

        if (eventType === EVENT_TYPES.singleDay || eventType === EVENT_TYPES.continuous) {
            return `${startTime} - ${endTime}`;
        } else {
            return (
                <div style={{ display: "flex", gap: "5px" }}>
                    {!showTickets ? (
                        <>
                            <Body2>Multiple Days</Body2>
                            <Body2
                                color="var(--tertiary-color)"
                                style={{ cursor: "pointer" }}
                                onClick={scrollToEventSchedule}
                            >
                                [View Event Schedule]
                            </Body2>
                        </>
                    ) : (
                        <Body2>Multiple Days</Body2>
                    )}
                </div>
            );
        }
    }, [eventData, showTickets]);


    /**
     * Get the event duration information.
     * Wrapped in useCallback to memoize the function and prevent unnecessary re-creations on every render.
     */
    const eventDurationUI = useCallback(() => {
        if (!eventData) return null;

        const {
            event_type: eventType,
            start_date: startDate,
            end_date: endDate,
            start_time: startTime,
            end_time: endTime,
            schedules
        } = eventData;

        const startDateTime = dayjs(`${startDate} ${startTime}`);
        const endDateTime = dayjs(`${endDate} ${endTime}`);
        const durationMinutes = endDateTime.diff(startDateTime, "minute");

        // Handle Single-Day Event
        if (eventType === EVENT_TYPES.singleDay) {
            const firstSchedule = schedules?.[0];

            if (firstSchedule?.slots?.length > 0) {
                return (
                    <Fragment>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            {!showTickets ? (
                                <>
                                    <Body2>Multiple Slots</Body2>
                                    <Body2
                                        color="var(--tertiary-color)"
                                        style={{ cursor: 'pointer' }}
                                        onClick={scrollToEventSchedule}
                                    >
                                        [View Event Schedule]
                                    </Body2>
                                </>
                            ) : (
                                <Body2>Multiple Slots</Body2>
                            )}
                        </div>
                    </Fragment>
                );
            }

            return <Body2>{durationMinutes} minutes</Body2>;
        }

        if (eventType === EVENT_TYPES.continuous) {
            const hours = Math.floor(durationMinutes / 60);
            const minutes = durationMinutes % 60;
            return <Body2>{hours > 0 ? `${hours} hour ` : ""}{minutes > 0 ? `${minutes} minutes` : ''}</Body2>;
        }

        // For other/unknown case
        return null;
    }, [eventData, showTickets])

    // console.log("Event Data -> ", eventData)

    /**
     * Handles the RSVP action from the user.
     * Sets a temporary response message and hides the RSVP UI.
     * @param {boolean} isAttending - Whether the user is attending the event.
     */
    function handleRsvp(isAttending) {
        const response = isAttending ? "yes" : "no";

        dispatch(rsvpToEvent({ response }))
            .then((response) => {
                // console.log("RSVP Response -> ", response?.payload);
                if (response?.payload?.code === 200) {
                    setShowRsvpResponseMessage(true);
                    dispatch(setInviteLinkUserDetails({
                        type: 'attendee',
                        response: response?.payload?.data?.response
                    }));
                    setTimeout(() => {
                        setShowRsvpResponseMessage(false);
                    }, 4000); // Hide message after 4 seconds
                }
            })
            .catch((error) => {
                console.error("RSVP Error -> ", error);
            })
            .finally(() => {
                setShowRsvp(false);
            });
    }

    async function handleMarkAsInterested() {
        try {

            if (!user || !user?.id) {
                openAuth();
                return;
            }

            const payload = {
                user_id: user?.id,
                entity: "event",
                entity_id: eventData?.eventId,
                interested: !isUserMarkEventAsInterested
            };

            const response = await dispatch(markEventAsInterested(payload)).unwrap();

            if (response?.code === 200) {
                setIsUserMarkEventAsInterested(!isUserMarkEventAsInterested);
                successToastTopRight(
                    !isUserMarkEventAsInterested
                        ? "You have marked this event as interested."
                        : "You have removed this event from interested."
                );

                // Refresh the user interested events
                // if (user?.id) {
                //     dispatch(getUserInterestedEventsByUserId(user?.id));
                // }
            }


            // console.log("Response -> ", response);

        } catch (error) {
            console.error("Mark as Interested Error -> ", error);
            errorToast(error?.message || "An unexpected error occurred while marking interest.");
        }
    }

    useEffect(() => {
        if (eventData && userInterestedEvents && userInterestedEvents.length > 0) {
            const isInterested = userInterestedEvents.some(
                (item) => item.entity === "event" && item.entity_id === eventData?.eventId
            );
            setIsUserMarkEventAsInterested(isInterested);
        } else {
            setIsUserMarkEventAsInterested(false);
        }
    }, [eventData, userInterestedEvents]);

    /**
     * Returns the needed state and function.
     */
    return {
        showRsvp,
        error,
        eventData,
        loading,
        eventDate,
        eventTimingUI,
        fcs,
        minimumTicketPrice,
        eventDurationUI,
        eventScheduleRef,
        getTickets,
        showTickets,
        handleRsvp,
        showRsvpResponseMessage,
        inviteLinkUserDetails,
        setShowRsvp,
        isUserMarkEventAsInterested,
        handleMarkAsInterested
    }
}